import { FC, useEffect, useState } from 'react';

import { useLogout } from './useLogout';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '@workos-inc/authkit-react';

import { ArrowDownRightIcon, ArrowUpLeftIcon } from '@/assets/icons';
import { LanguageCode } from '@/enums';
import { captureAsyncError } from '@/utils/captureAsyncError';
import { cn } from '@/utils/tailwind';

type JwtPayload = {
  act?: {
    sub: string;
  };
  user_language: LanguageCode;
  aud: string;
  first_name: string;
  last_name: string;
  org_id: string;
  email: string;
  iss: string;
  sub: string;
  sid: string;
  jti: string;
  role: string;
  permissions: string[];
  feature_flags: string[];
  exp: number;
  iat: number;
};

export const ImpersonationWrapper: FC = () => {
  const { user, getAccessToken } = useAuth();
  const logout = useLogout();
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleStopImpersonation = () => {
    logout();
  };

  useEffect(() => {
    const checkToken = async () => {
      if (!user) return;
      try {
        const token = await getAccessToken();

        if (!token) return;

        const decoded = jwtDecode<JwtPayload>(token);
        if (decoded.act) {
          setIsImpersonating(true);
        } else {
          setIsImpersonating(false);
        }
      } catch (err) {
        console.error('Failed to decode token', err);
      }
    };
    void checkToken().catch(captureAsyncError);
  }, [user, getAccessToken]);

  if (!isImpersonating) return null;

  return (
    <>
      <div
        className={cn(
          'fixed z-50 pointer-events-none transition-all duration-500',
          collapsed ? '-inset-[5px]' : 'inset-0'
        )}
      >
        <div
          className={cn(
            'absolute inset-0 border-4 border-yellow-300 pointer-events-none transition-all duration-500'
          )}
        />
        <div
          className={cn(
            'absolute inset-[4px] border border-yellow-500 pointer-events-none transition-all duration-500'
          )}
        />
      </div>

      <div
        className={cn(
          'fixed bottom-1 left-1/2 z-[9999] bg-yellow-300 border-t border-l border-r border-yellow-500 text-body-12-regular px-1 pt-1 flex items-center gap-4 transition-all duration-500',
          collapsed
            ? 'opacity-0 pointer-events-none -translate-x-[45%]'
            : 'opacity-100 -translate-x-1/2'
        )}
      >
        <span className="ml-1 text-custom-warm-grey-900">
          You are impersonating <span className="text-body-14-semibold">{user?.email}</span>
        </span>
        <div className="flex items-center gap-1">
          <button
            className="cursor-pointer px-2 py-1 bg-black text-white rounded-[4px]"
            onClick={handleStopImpersonation}
          >
            Stop
          </button>
          <button
            className="cursor-pointer px-2 py-1 bg-black text-white rounded-[4px]"
            onClick={() => setCollapsed(true)}
          >
            <ArrowDownRightIcon width={16} height={16} />
          </button>
        </div>
      </div>
      <div
        className={cn(
          'fixed bottom-1 right-1 z-[9999]  size-8 flex items-center justify-center rounded-[4px] bg-yellow-300 border border-yellow-500 transition-all duration-500',
          collapsed ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 pointer-events-none'
        )}
      >
        <button
          className="bg-custom-warm-grey-900 p-1 cursor-pointer rounded-[4px]"
          onClick={() => setCollapsed(false)}
        >
          <ArrowUpLeftIcon className="text-white" width={16} height={16} />
        </button>
      </div>
    </>
  );
};
